import { UntitledIcon } from '@faceup/icons'
import { usAttachment02 } from '@faceup/icons/usAttachment02'
import { Button, Space, Tooltip, Typography, useUiBaseConfig } from '@faceup/ui-base'
import type { ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { ucShieldSuccess, ucShieldWarning } from '../customIcons'

const { Text } = Typography

export type Attachment = {
  file: File
  compareKey: string
  loading: boolean
  processed: boolean
}

export type AlreadyUploadedAttachment = {
  id: string
  name: string
}

export type EditingAttachmentsProps = {
  alreadyUploadedAttachments?: AlreadyUploadedAttachment[]
  markAttachmentForDeletion?: (attachmentId: string) => void
}

export type NotEditingAttachmentsProps = {
  alreadyUploadedAttachments?: never
  markAttachmentForDeletion?: never
}

export type AttachmentUploadListProps = {
  translations: {
    metadataRemoved: string
    metadataUnableToRemove: string
    delete: string
  }
  attachments: Attachment[]
  setAttachments: (attachments: Attachment[]) => void
} & (EditingAttachmentsProps | NotEditingAttachmentsProps)

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const AttachmentWrap = ({ className, ...props }: ComponentProps<'div'>) => (
  <div
    className={twMerge('flex items-center justify-between py-2 ps-4 pe-2', className)}
    {...props}
  />
)

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const CenteredIcon = ({ className, ...props }: ComponentProps<typeof UntitledIcon>) => (
  <UntitledIcon className={twMerge('flex text-colorTextTertiary text-xl', className)} {...props} />
)

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const LimitedSpace = ({ className, ...props }: ComponentProps<typeof Space>) => (
  <Space
    className={twMerge('max-w-[calc(100%-150px)] [&_.ant-space-item]:max-w-full', className)}
    {...props}
  />
)

// TODO: styled leftover. Reused element extracted so its shared classes live in one
// place. Ideally done differently, kept like this to minimize breaking changes.
const FileName = ({ className, ...props }: ComponentProps<typeof Text>) => (
  <Text
    className={twMerge(
      'sensitive block overflow-hidden text-ellipsis whitespace-nowrap font-semibold',
      className
    )}
    {...props}
  />
)

export const AttachmentUploadList = ({
  alreadyUploadedAttachments = [],
  markAttachmentForDeletion = () => undefined,
  attachments,
  setAttachments,
  translations,
}: AttachmentUploadListProps) => {
  const { token } = useUiBaseConfig()
  const deleteAttachment = (index: number) => {
    if (attachments.every(a => !a.loading)) {
      setAttachments(attachments.filter((_, i) => i !== index))
    }
  }

  if (!attachments.length && !alreadyUploadedAttachments.length) {
    return null
  }

  return (
    <div className='rounded-md border border-colorBorderSecondary [&>div:last-child]:border-b-0 [&>div]:border-colorBorderSecondary [&>div]:border-b'>
      {alreadyUploadedAttachments.map(attachment => (
        <AttachmentWrap key={attachment.id}>
          <Tooltip placement='top' title={translations.metadataRemoved}>
            <LimitedSpace>
              <CenteredIcon icon={usAttachment02} />
              <FileName type='success'>{attachment.name}</FileName>
              <CenteredIcon icon={ucShieldSuccess} color={token.green5} />
            </LimitedSpace>
          </Tooltip>
          <Button
            size='small'
            type='link'
            danger
            onClick={() => markAttachmentForDeletion(attachment.id)}
          >
            {translations.delete}
          </Button>
        </AttachmentWrap>
      ))}
      {attachments.map((attachment, index) => (
        <AttachmentWrap key={attachment.compareKey}>
          <Tooltip
            placement='top'
            title={
              attachment.processed
                ? translations.metadataRemoved
                : translations.metadataUnableToRemove
            }
            {...(attachment.loading && { open: false })}
          >
            <LimitedSpace>
              <CenteredIcon icon={usAttachment02} />
              <FileName type={attachment.processed ? 'success' : 'warning'}>
                {attachment.file.name}
              </FileName>
              <CenteredIcon
                icon={attachment.processed ? ucShieldSuccess : ucShieldWarning}
                color={attachment.processed ? token.green5 : token.orange5}
              />
            </LimitedSpace>
          </Tooltip>
          <Button
            type='link'
            danger
            size='small'
            loading={attachment.loading}
            onClick={() => deleteAttachment(index)}
          >
            {translations.delete}
          </Button>
        </AttachmentWrap>
      ))}
    </div>
  )
}
